import type { IndustryPage } from "@/lib/data";

export const productManufacturers: IndustryPage = {
  slug: "product-manufacturers",
  seoTitle:
    "Building Products Website Design UK | Built for Specifiers",
  seoDescription:
    "Building products website design: product ranges, technical data sheets, BIM/CAD downloads and stockists. Fixed £1,000, live in 7 days. Book a free call.",
  serviceName: "Website design for building product manufacturers",
  tldr:
    "ConstructionWebDesign.uk offers building products website design for UK manufacturers: product range pages, technical data sheets, BIM and CAD downloads and a stockist finder, built for specifiers, architects and contractors. The Website package is £1,000, a one-off fixed price with no subscription, or £1,399 with logo and branding. The site is live in 7 days, and you own the website.",
  pricingFromGBP: 1000,
  hero: {
    eyebrow: "Manufacturer website design · UK",
    headline: "Website design for building product manufacturers, built for the spec.",
    highlight: "built for the spec",
    sub: "Websites for UK building product manufacturers with a page per product, technical data sheets, NBS-ready specification clauses, BIM and CAD downloads, compliance documents, sample and CPD requests and a stockist finder.",
    secondaryCta: { label: "See B2B demos", href: "#demos" },
  },
  slides: [
    {
      bg: "https://constructionwebdesign.cdn.modulify.website/images/industry-hero/Carpenters%20%26%20Joiners.jpg",
      alt: "Timber components being machined and finished on a workshop bench",
      headlineTop: "Get specified,",
      headlineBottom: "not just quoted.",
      preview: "https://constructionwebdesign.cdn.modulify.website/images/demo-fullheight/stackline.jpg",
    },
  ],
  demoSlugs: ["stackline", "coreframe", "avelon"],
  demosHeading: "Manufacturer website demo templates.",
  demosSub:
    "There is no manufacturer demo template yet, so these are the closest demo templates in structure and restraint. The product pages, technical downloads and stockist finder are built on a layout like these. They are demo templates, not client websites.",
  value: {
    eyebrow: "Why it matters",
    heading: "Why does a building product manufacturer need a website?",
    intro:
      "A building product manufacturer needs a website because architects and specifiers choose products at their desks, well before a contractor places an order. A manufacturer website puts the product data, drawings and downloads they need in front of them while they write the specification.",
    points: [
      {
        title: "Get named in the specification.",
        body: "Specifiers write clauses in NBS with product data open beside them. A ready-made clause, product reference and performance data make it easier to name your product, rather than a generic description a contractor could swap at tender.",
        icon: "Compass",
      },
      {
        title: "Make downloads easy to get.",
        body: "BIM objects, DWG details, technical data sheets, Declarations of Performance and installation guides with no login and no 'contact us for details'. A specifier who cannot get a file quickly can use a competitor's file instead.",
        icon: "Check",
      },
      {
        title: "Serve specifiers and buyers differently.",
        body: "The architect wants performance, fire classification and detail drawings. The contractor wants lead times, pack sizes and stockists. Two routes through the same product ranges mean neither has to dig through the other's information.",
        icon: "Buildings",
      },
      {
        title: "Support the spec at value engineering.",
        body: "Products get swapped when a job is value engineered. Published test data, warranty terms and an 'or equal' comparison page give your specifier the evidence to hold the specification without having to ring you first.",
        icon: "Shield",
      },
    ],
  },
  features: {
    eyebrow: "What's in the site",
    heading: "What should a building products website include?",
    intro:
      "A building products website should include a page per product, technical data sheets, specification clauses, BIM and CAD downloads, compliance documents and a stockist finder. ConstructionWebDesign.uk plans each one into the sitemap before design starts, with one route for specifiers and one for contractors.",
    items: [
      {
        title: "Product range and product pages.",
        body: "Product ranges grouped the way specifiers look for them, by application, material or system, with one page per product. Each page carries performance figures, dimensions, finishes, fire classification, U-values or acoustic ratings where relevant, and the standards tested to. One page per product gives Google a page to show for each product name.",
      },
      {
        title: "Technical data sheets and compliance documents.",
        body: "Technical data sheets, Declarations of Performance (DoPs), UKCA and CE marking, BS EN test reports, Euroclass fire classifications, environmental product declarations (EPDs), warranty terms and installation guides. Current versions are dated, and superseded ones are archived rather than deleted.",
      },
      {
        title: "NBS-ready specification clauses.",
        body: "A copy-ready clause and product reference on each product page, set out so a specifier can lift it into NBS Chorus or a practice template, with a link to your NBS Source listing if you have one.",
      },
      {
        title: "BIM and CAD download library.",
        body: "Revit families, IFC objects and DWG or PDF details, versioned, named to a consistent convention and downloadable without a form. The library is a set of indexable pages, so a specifier searching for a file by product name can find it.",
      },
      {
        title: "Sample requests and CPD booking.",
        body: "A sample request form that asks for the project and its RIBA stage, and a CPD page where a practice can book a seminar. Both give a manufacturer time in front of a specifier before the drawings are finished.",
      },
      {
        title: "Stockist and distributor finder.",
        body: "For the contractor who has been handed your product name: where to buy it, in what pack sizes and with what lead time. A clear stockist route helps stop a specified product being swapped for whatever the merchant has in stock.",
      },
      {
        title: "Search foundations for product searches.",
        body: "On-page SEO for product names, applications and sectors, technical schema markup, and product data published as indexable pages rather than locked inside a PDF brochure.",
      },
    ],
  },
  faqs: [
    {
      q: "How much does a building products website cost in the UK?",
      a: "A building products website from ConstructionWebDesign.uk costs £1,000, a one-off fixed price with no subscription, or £1,399 with logo and branding. Typical UK ranges for comparison: a freelancer charges about £4,000–£5,000 and a web design agency £5,000–£25,000 over 8–12 weeks. The pages and downloads that go in at launch are agreed in the strategy and sitemap step, and the manufacturer owns the website.",
    },
    {
      q: "How long does it take to build a manufacturer website?",
      a: "A manufacturer website from ConstructionWebDesign.uk is live in 7 days from the discovery call. The five steps are discovery call, strategy and sitemap, design, build, then launch and support. Having current technical data sheets, DoPs, test certificates and BIM or CAD files ready before the call keeps the week on track, because the product pages are built from those documents.",
    },
    {
      q: "What should a construction product website include?",
      a: "A construction product website should include product range pages with one page per product, technical data sheets, compliance documents such as Declarations of Performance, NBS-ready specification clauses, BIM and CAD downloads, sample and CPD requests and a stockist finder. ConstructionWebDesign.uk sets these out as two routes: technical data for specifiers and architects, and stockists and lead times for contractors.",
    },
    {
      q: "Does a building product manufacturer need a website?",
      a: "Yes, a building product manufacturer needs a website because specifiers and architects look up product data, drawings and certificates while they write the specification. A manufacturer website that puts that information on clear product pages, with files that download without a form, makes your product easier to name. Without one, specifiers rely on whatever data they can find elsewhere.",
    },
    {
      q: "Can a manufacturer website host BIM objects and CAD files?",
      a: "Yes, a building products website from ConstructionWebDesign.uk can include a BIM and CAD download library: Revit families, IFC objects and DWG or PDF details, versioned and named to a consistent convention. Files download without a form or login. Superseded versions stay archived, so a specifier working from an older model can still find the file they used.",
    },
    {
      q: "What is an NBS specification clause and should it be on a manufacturer website?",
      a: "An NBS specification clause is the wording a specifier uses in NBS to name a product in the specification, and yes, it belongs on a manufacturer website. Publishing a copy-ready clause and product reference on each product page lets the specifier write your product in as you describe it. Without one, the specifier may write a generic description that a contractor can substitute against.",
    },
    {
      q: "Should technical downloads be gated behind a form?",
      a: "No, technical downloads such as BIM objects, CAD details and technical data sheets are usually better left ungated on a building products website. A specifier who hits a form can download a competitor's file instead. If you need to know who is interested, a sample request or CPD booking form captures named enquiries without blocking the files.",
    },
    {
      q: "How does a building products website get found on Google?",
      a: "A building products website gets found on Google through one indexable page per product, application and sector, rather than data locked inside a PDF brochure. ConstructionWebDesign.uk includes on-page SEO for product names and applications, plus technical schema markup, and the download library gives specifiers searching by product name a page to land on. No one can promise rankings, but a clear page structure gives Google the right pages to show.",
    },
  ],
  guides: [
    { href: "/blog/how-much-should-a-construction-website-cost", label: "How much does a construction website cost in the UK?" },
    { href: "/blog/online-presence-construction-company", label: "Online presence for construction companies: a guide" },
    { href: "/blog/how-to-get-more-construction-leads", label: "How to get more construction leads without paying" },
    { href: "/blog/wordpress-vs-custom-construction-website", label: "WordPress vs custom website for construction firms" },
  ],
  finalCta: {
    headline: "Plan your building products website.",
    highlight: "building products website",
    sub: "Book a free 30-minute call about your product ranges, downloads and stockists. The price is a fixed £1,000, and the site is live in 7 days.",
  },
};
